import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'

import './styles.css'
import './i18n'
import { markNativeShell } from '@/lib/native-shell'
import { initMonitoring } from '@/lib/monitoring'
import { queryClient } from '@/lib/query-client'
import { routeTree } from './routeTree.gen'

const router = createRouter({ routeTree })

// 路由类型注册：让 TanStack Router 的路径与查询参数都进入类型检查
// (机票搜索参数多，这层校验是白捡的)
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

/** 按环境变量决定是否挂 MSW。后端接口没建成的阶段靠它顶着。 */
async function enableMocking(): Promise<void> {
  if (import.meta.env.VITE_ENABLE_MSW !== 'true') return
  const { worker } = await import('./mocks/browser')
  await worker.start({ onUnhandledRequest: 'bypass' })
}

/**
 * 壳专用样式的开关（W6）。
 *
 * ⚠ 放在 render 之前、且在 `enableMocking()` 之外：MSW 的启动是异步的，
 *   等它 resolve 再加类就晚了一帧 —— 那一帧里整页橡皮筋是能拉动的。
 */
markNativeShell()

/**
 * 错误上报（W7）。⚠ 放在 render **之前**：渲染期间就炸的错误也要能被捕到。
 * ⚠ 没配 `VITE_SENTRY_DSN` 时它整体 no-op —— dev 和 e2e 都不会往外发东西。
 */
initMonitoring()

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('找不到 #root 挂载点，index.html 被改坏了')

void enableMocking().then(() => {
  createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  )
})
